import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { DeviceContext } from "../context/deviceContext";
import SearchAndFilter from "../searchandfilter/searchandfilter";
import CalculateAuctionTime from "../utility/calculateAuctionTime";
import { SearchAndFilterContext } from "../context/searchContext";
import { getData } from "../fetchRoutes/getData";
import PropertyImageHandler from "../utility/imageHandler";
import PriceProp from "../props/priceProp.js";
import PropertyAddressProp from "../props/addressProp.js";
import PropertyDescriptionProp from "../props/descProp.js";

export default function PropertyContainer() {
    
    const getAllProperties = process.env.REACT_APP_GET_PROPERTIES;

    const { isMobile } = useContext(DeviceContext);
    const { handleFilteration } = useContext(SearchAndFilterContext);

    const [properties, setProperties] = useState([]);
    const [visibleProperties, setVisibleProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFetched, setIsFetched] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 4; // Load 4 properties at a time

    const loaderRef = useRef(null);

    const isAuction = false;
    const hasFiltered = useRef(false);

    // Fetch all properties
    useEffect(() => {
        const fetchProperties = async () => {
            if (!getAllProperties) return;
            setLoading(true);
            try {
                const response = await getData(getAllProperties);
                if (response.status === "Success") {
                    setProperties(response.data);
                }
            } catch (err) {
                console.error("Error fetching properties:", err);
            } finally {
                setLoading(false);
                setIsFetched(true);
            }
        };

        if (!isFetched) {  
            fetchProperties();
        }
    }, [getAllProperties, isFetched]);

    // Handle filtering
    useEffect(() => {
        if (properties.length > 0 && !hasFiltered.current) {
            handleFilteration(setProperties);
            hasFiltered.current = true;
        }
    }, [properties, handleFilteration]);


    // Load more properties
    const loadMoreProperties = useCallback(() => {
        const nextPage = currentPage + 1;
        const startIndex = (nextPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        const nextProperties = properties.slice(startIndex, endIndex);

        if (nextProperties.length > 0) {
            setVisibleProperties((prev) => [...prev, ...nextProperties]);
            setCurrentPage(nextPage);
        }
    }, [currentPage, properties, pageSize]);

    // Initialize the first batch of properties
    useEffect(() => {
        if (properties.length > 0) {
            setVisibleProperties(properties.slice(0, pageSize));
        }
    }, [properties, pageSize]);

    
    // Intersection Observer for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreProperties();
                }
            },
            { threshold: 1.0 }
        );

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => {
            if (loaderRef.current) {
                // eslint-disable-next-line
                observer.unobserve(loaderRef.current);
            }
        };
    }, [loadMoreProperties]);



    return (
        <>
        <main className="propertiesPage">
            <SearchAndFilter 
                setFilteredProperties={setVisibleProperties}
                properties={properties}
            />

            <div className="properties">
                {loading ? (
                    <div className="loaderContainer">
                        <div className="loader"></div>
                        <p className="loadingText">Loading...</p>
                    </div>
                ) : visibleProperties.length > 0 ? (
                    visibleProperties.map((property, index) => (
                        <div className="propertyCards" key={index}>
                            <PropertyImageHandler property={property}/>
                            <div className="details">

                                <PriceProp
                                    mainClassName="price"
                                    spanClassname1="propertyPrice"
                                    spanClassname2="favorite"
                                    property={property}
                                />

                                <PropertyAddressProp
                                    mainClassName="roomAddress" 
                                    roomClassName="houseRoomsDetails" 
                                    addressClassName="propertyAddress"
                                    property={property} 
                                    isProperty={false}
                                />
                                <PropertyDescriptionProp 
                                    mainClassName="description" 
                                    textClassName="propertyDescription" 
                                    property={property} 
                                    isMobile={isMobile} 
                                />
                                {property.auction?.status === "closed" ? (
                                    <p className="notOnAuction auctionParagraph ">Auction Closed</p>
                                ) : (
                                    <CalculateAuctionTime 
                                        property={property}
                                        auctionStartTime={property.auction?.startTime || 0} 
                                        auctionEndTime={property.auction?.endTime || 0}
                                        title={property.title}
                                        description={property.description}
                                        location={property.address}
                                        isAuction={isAuction}  
                                    />
                                )
                                }
                                
                            </div>
                        </div>
                    ))
                ) : (
                    <p>No open properties available.</p>
                )}
            </div> 
            
            {/* Loader element to trigger lazy loading */}
            <div ref={loaderRef} style={{ height: "50px", margin: "20px 0" }}>
                {loading && <p>Loading more properties...</p>}
            </div>

        </main>
        </>
    );
}
